import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MapPin, Phone } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/products', label: 'Products & Services' },
    { path: '/location', label: 'Hours & Location' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#1A4D2E] text-white py-2 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <MapPin size={14} />
              14346 Avenue 384, Yettem, CA 93292
            </span>
            <span className="flex items-center gap-2">
              <Phone size={14} />
              (509) 405-7493
            </span>
          </div>
          <div className="open-badge">
            Open Now
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg md:text-xl">SM</span>
              </div>
              <div>
                <h1 className="font-bold text-lg md:text-xl text-[#1A4D2E] leading-tight">
                  Shah Market
                </h1>
                <p className="text-xs text-[#5F6C7B] hidden sm:block">Your Neighborhood Store</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Link
                to="/location"
                className="btn-primary px-5 py-2.5 rounded-xl font-medium inline-flex items-center gap-2"
                data-testid="nav-cta-button"
              >
                <MapPin size={18} />
                Find Us
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-[#F4F1DE] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
        data-testid="mobile-menu"
      >
        <div className="flex justify-between items-center mb-8">
          <h2 className="font-bold text-xl text-[#1A4D2E]">Menu</h2>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-[#F4F1DE]"
          >
            <X size={24} />
          </button>
        </div>
        <div className="flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-lg font-medium py-3 border-b border-[#F4F1DE] ${
                isActive(link.path) ? 'text-[#1A4D2E]' : 'text-[#3D405B]'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
        </div>
        <div className="mt-8 space-y-4">
          <div className="flex items-center gap-3 text-[#5F6C7B]">
            <MapPin size={18} />
            <span>14346 Avenue 384, Yettem, CA</span>
          </div>
          <div className="flex items-center gap-3 text-[#5F6C7B]">
            <Phone size={18} />
            <span>(509) 405-7493</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
